import {
  Box,
  Button,
  Card,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Stack,
  Grid2
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BtnTrash from '../../../../../components/bonus/ui/BtnTrash';
import ConfirmDialog from '../../../../../components/shared/ConfirmDialog';
import useBootstrapSize from '../../../../../hooks/useBootstrapSize';
import dayjs from 'dayjs';
import { dispatchCusEvent, getOnlyData } from '../../../../../utils';
import ShowContainer from '../../../../../components/material-ui/ShowContainer';
import Table from '../../../../../components/material-ui/adaptive/Table';
import MyTableCell from '../../../../../components/material-ui/adaptive/MyTableCell';
import CusLabelSelect from '../../../../../components/material-ui/CusLabelSelect';
import { API_GROUP } from '../../../../../api/user';
import { ENUM_CUS_EVENT } from '../../../../../enums/enums';
import CurrencySelect, { ENUM_CURRENCY } from '../../../../../components/shared/CurrencySelect';
import MyTableBoldCell from '../../../../../components/material-ui/adaptive/MyTableBoldCell';

const ENUM_STATUS = {
  all: 'all',
  active: 'active',
  inactive: 'inactive',
  deleted: 'deleted'
};

export default function FSBonusList({ setOpenInfoId, allBonuses, deleteBonus, groupId }) {
  const { t } = useTranslation();

  const statusOptions = [
    { label: t('all'), value: 'all' },
    { label: t('active'), value: 'active' },
    { label: t('inactive'), value: 'inactive' },
    { label: t('deleted'), value: 'deleted' }
  ];

  const { isLessTablet, isMobile } = useBootstrapSize();
  const [openDelete, setOpenDelete] = useState(false);
  const [bonuses, setBonuses] = useState(allBonuses);
  const [fsStatus, setFsStatus] = useState(statusOptions[1].value);
  const [selectCurrency, setSelectCurrency] = useState(ENUM_CURRENCY.all);

  useEffect(() => {
    const getBonusesStatus = () => {
      if (fsStatus === ENUM_STATUS.all) return allBonuses;
      return allBonuses.filter((el) => el.status == fsStatus);
    };

    const getBonusesCurrency = (bonuses) => {
      if (selectCurrency === ENUM_STATUS.all) return bonuses;
      return bonuses.filter((el) => el.currency == selectCurrency);
    };

    const newBonusesStatus = getBonusesStatus();
    const newBonuses = getBonusesCurrency(newBonusesStatus);
    setBonuses(newBonuses);
  }, [allBonuses, fsStatus, selectCurrency]);

  const onDeleteClick = (bonusInfoId) => setOpenDelete(bonusInfoId);

  const onConfirmDelete = (bonusId) => {
    setOpenDelete(false);
    deleteBonus(bonusId);
  };

  const onRowClick = (id) => {
    setOpenInfoId(id);
  };

  const getData = (data) => {
    if (!data) return undefined;
    return getOnlyData(dayjs(data), '', 'DD.MM.YYYY');
  };

  const onEnabled = (bonusInfo) => {
    API_GROUP.bonus
      .enable({ bonusId: bonusInfo.id, enabled: true })
      .then((e) => {
        dispatchCusEvent(ENUM_CUS_EVENT.getBonusList, { groupId });
      })
      .catch(console.error);
  };

  // const showBonuses =
  //   bonuses?.length || fsStatus !== ENUM_STATUS.all || selectCurrency !== ENUM_CURRENCY.all;

  return (
    <ShowContainer condition={allBonuses.length}>
      <Card variant="outlined">
        <Grid2 spacing={2} container>
          <CusLabelSelect
            label={'Status'}
            id={'freespin-status'}
            options={statusOptions}
            value={fsStatus}
            onChange={setFsStatus}
            isRow
            fullWidth={false}
            marginRight={0}
          />

          <CurrencySelect value={selectCurrency} setValue={setSelectCurrency} isChooseAll isRow />
        </Grid2>

        <ShowContainer
          condition={bonuses?.length}
          plug={
            <Box p={4} display={'flex'} alignItems={'center'} justifyContent={'center'} height={20}>
              <Typography variant="caption">
                {fsStatus !== ENUM_STATUS.all
                  ? t('No bonuses with status', { status: t(fsStatus) })
                  : t('No bonuses with currency', { currency: selectCurrency })}
              </Typography>
            </Box>
          }>
          <TableContainer sx={{ minWidth: { sm: '350px' } }}>
            <Table>
              <TableHead>
                <TableRow>
                  <MyTableBoldCell>{isLessTablet ? t('#id') : t('#id bonus')}</MyTableBoldCell>
                  <MyTableBoldCell>{t('FS bonus name')}</MyTableBoldCell>
                  <MyTableBoldCell>{t('Currency')}</MyTableBoldCell>
                  <MyTableBoldCell>{t('Status')}</MyTableBoldCell>
                  <MyTableBoldCell>
                    <Stack display={'flex'} flexDirection={'column'} gap={1}>
                      <span>{t('Launches')}</span>
                    </Stack>
                  </MyTableBoldCell>
                  <MyTableBoldCell>{t('Available')}</MyTableBoldCell>
                  <MyTableCell></MyTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bonuses.map((bonusInfo) => {
                  const activated = getData(bonusInfo.activated);
                  const expired = getData(bonusInfo.expired);
                  const availableTime =
                    activated && expired ? (
                      <>
                        {activated} - <br />
                        {expired}
                      </>
                    ) : (
                      ''
                    );

                  const isDelete = bonusInfo.status === ENUM_STATUS.deleted;

                  return (
                    <TableRow
                      key={bonusInfo.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => onRowClick(bonusInfo.id)}>
                      <MyTableBoldCell>{bonusInfo.id}</MyTableBoldCell>
                      <MyTableCell>{bonusInfo.name}</MyTableCell>
                      <MyTableCell>{bonusInfo.currency}</MyTableCell>
                      <MyTableCell>{t(bonusInfo.status)}</MyTableCell>
                      <MyTableCell type="numeric">{`${bonusInfo.startCount}`}</MyTableCell>
                      <MyTableCell>{availableTime}</MyTableCell>
                      <ShowContainer condition={!isDelete}>
                        <MyTableCell style={{ paddingLeft: 0 }}>
                          <Stack
                            display={'flex'}
                            flexDirection={isMobile ? 'column' : 'row'}
                            gap={0.5}>
                            <BtnTrash
                              onDelete={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                onDeleteClick(bonusInfo.id);
                              }}
                            />
                            <ShowContainer condition={!bonusInfo.enabled}>
                              <Button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  onEnabled(bonusInfo);
                                }}>
                                {t('Enable')}
                              </Button>
                            </ShowContainer>
                          </Stack>
                        </MyTableCell>
                      </ShowContainer>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </ShowContainer>
      </Card>

      <ConfirmDialog
        open={openDelete}
        title={t('Confirm deletion')}
        content={t('Are you sure you want to delete this item? This action cannot be undone.')}
        onClose={() => setOpenDelete(false)}
        onConfirm={() => onConfirmDelete(openDelete)}
      />
    </ShowContainer>
  );
}
